import {
  BaseTransactionDetailsDto,
  GetBankTransferDto,
  GetRechnungDetailsDto,
} from "../../../../../api-client";
import notificationStore from "../../../../../stores/Notification/notificationStore";
import {
  useCashCancellation,
  useECCancellation,
  useBankTransferCancellation,
} from "../../../../../stores/Transactions/transaction.mutations";
import useStore from "../../../../../helpers/useStore";
import { TransactionsContext } from "../../../../../stores/Transactions/Transactions.provider";

interface UseTransactionHandlersProps {
  onRefresh?: () => void;
}

/**
 * Custom hook consolidating all transaction operation handlers
 * Reduces duplication and follows DRY principle
 */
export const useTransactionHandlers = ({
  onRefresh,
}: UseTransactionHandlersProps) => {
  const transactionStore = useStore(TransactionsContext);
  const { softDeleteRechnung, createBillReminder } = transactionStore;

  // Mutations
  const cashCancellation = useCashCancellation();
  const ecCancellation = useECCancellation();
  const bankTransferCancellation = useBankTransferCancellation();

  // ==================== CASH HANDLERS ====================
  const handleCashCancellation = async (
    transaction: BaseTransactionDetailsDto,
    reason: string,
  ) => {
    if (!transaction) return;

    try {
      await cashCancellation.mutateAsync({
        transactionId: transaction.id,
        reason: reason,
        patientId: transaction.patient?.id,
        rxId: transaction.rx?.id,
      });
      onRefresh?.();
    } catch (error) {
      console.error("Error cancelling cash transaction:", error);
      notificationStore.showMessage(
        "Fehler beim Stornieren der Bargeldtransaktion",
        "error",
      );
    }
  };

  // ==================== EC HANDLERS ====================
  const handleECCancellation = async (
    transaction: BaseTransactionDetailsDto,
    reason: string,
  ) => {
    if (!transaction) return;

    try {
      await ecCancellation.mutateAsync({
        transactionId: transaction.id,
        reason: reason,
        patientId: transaction.patient?.id,
        rxId: transaction.rx?.id,
      });
      onRefresh?.();
    } catch (error) {
      console.error("Error cancelling EC transaction:", error);
      notificationStore.showMessage(
        "Fehler beim Stornieren der EC-Transaktion",
        "error",
      );
    }
  };

  // ==================== BANK TRANSFER HANDLERS ====================
  const handleBankTransferCancellation = async (
    transaction: GetBankTransferDto,
    reason: string,
  ) => {
    if (!transaction) return;

    try {
      await bankTransferCancellation.mutateAsync({
        bankTransferId: transaction.id,
        reason: reason,
        patientId: transaction.patientId,
        rxId: transaction.rxId,
      });
      onRefresh?.();
    } catch (error) {
      console.error("Error cancelling bank transfer:", error);
      notificationStore.showMessage(
        "Fehler beim Stornieren der Überweisung",
        "error",
      );
    }
  };

  // ==================== RECHNUNG HANDLERS ====================
  const handleRechnungDeletion = async (rechnung: GetRechnungDetailsDto) => {
    if (!rechnung) return;

    try {
      await softDeleteRechnung(rechnung.id);
      onRefresh?.();
    } catch (error) {
      console.error("Error deleting rechnung:", error);
      notificationStore.showMessage(
        "Fehler beim Löschen der Rechnung",
        "error",
      );
    }
  };

  const handleCreateReminder = async (rechnung: GetRechnungDetailsDto) => {
    try {
      if (!rechnung) return;
      await createBillReminder(rechnung.id);
      onRefresh?.();
    } catch (error) {
      console.error("Error creating reminder:", error);
      notificationStore.showMessage(
        "Fehler beim Erstellen der Mahnung",
        "error",
      );
    }
  };

  return {
    // Cash
    handleCashCancellation,
    cashCancellationLoading: cashCancellation.isPending,

    // EC
    handleECCancellation,
    ecCancellationLoading: ecCancellation.isPending,

    // Bank Transfer
    handleBankTransferCancellation,
    bankTransferCancellationLoading: bankTransferCancellation.isPending,

    // Rechnung
    handleRechnungDeletion,
    handleCreateReminder,
  };
};
